import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import { useI18n } from "../context/I18nContext";

const formatAmount = (a, c) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: c || "EUR", maximumFractionDigits: 0 }).format(a || 0);

export default function ProposalView() {
  const { token } = useParams();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [decision, setDecision] = useState(null);
  const [note, setNote] = useState("");
  const [status, setStatus] = useState("idle");

  const load = () => {
    api.get(`/quotes/by-token/${token}`).then((r) => setData(r.data))
       .catch((e) => setErr(e?.response?.data?.detail || "Lien invalide"));
  };
  useEffect(() => { load(); }, [token]);

  const decide = async (d) => {
    setDecision(d);
    setStatus("sending");
    try {
      await api.post(`/quotes/by-token/${token}/decision`, { decision: d, note });
      setStatus("done");
      load();
    } catch (e) {
      setStatus("err");
      setErr(e?.response?.data?.detail || "Erreur");
    }
  };

  if (err && !data) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="proposal-invalid">
        <div className="overline mb-3 text-red-400">Lien invalide</div>
        <h1 className="serif text-3xl">Ce lien n'est plus actif.</h1>
      </div>
    );
  }
  if (!data) return <div className="p-24 text-center">{t("common.loading")}</div>;

  if (!data.proposal) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="proposal-pending">
        <div className="overline mb-3 text-[var(--gai-red)]">En cours de préparation</div>
        <h1 className="serif text-4xl">Votre proposition est en cours de finalisation.</h1>
        <p className="mt-4 text-[var(--gai-grey-700)]">Vous recevrez ce lien à nouveau dès que le devis sera prêt.</p>
      </div>
    );
  }

  const decided = data.proposal_decision?.decision;
  const p = data.proposal;

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16" data-testid="page-proposal">
      <div className="overline mb-3">Étape 3/3 · Proposition commerciale</div>
      <h1 className="serif text-4xl md:text-5xl leading-[1.05]">Proposition GAI · {data.full_name}</h1>
      <p className="mt-3 text-[var(--gai-grey-600)]">
        {data.company ? `${data.company} · ` : ""}{data.email}
      </p>

      <div className="mt-10 border border-[var(--gai-grey-300)] p-8" data-testid="proposal-card">
        <div className="overline mb-2">Montant</div>
        <div className="serif text-5xl md:text-6xl gai-gradient-gold">{formatAmount(p.amount, p.currency)}</div>
        <div className="mt-4 text-[0.72rem] tracking-[0.2em] uppercase text-[var(--gai-grey-500)]">
          Validité : {p.validity_days} jours · envoyée le {new Date(p.sent_at).toLocaleDateString("fr-FR")}
        </div>

        <div className="gai-hairline my-8" />

        <div className="overline mb-3">Description</div>
        <p className="text-[var(--gai-grey-800)] leading-relaxed whitespace-pre-line" data-testid="proposal-description">{p.description}</p>

        {p.deliverables && p.deliverables.length > 0 && (
          <>
            <div className="overline mt-8 mb-3">Livrables</div>
            <ul className="space-y-2">
              {p.deliverables.map((d, i) => (
                <li key={i} className="flex gap-3 text-[var(--gai-grey-800)]">
                  <span className="text-[var(--gai-red)] mt-1">▪</span>
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {decided ? (
        <div className="mt-10 p-8 border border-[var(--gai-grey-300)] text-center" data-testid="proposal-decided">
          <div className="overline mb-3" style={{ color: decided === "accepted" ? "var(--gai-red)" : "#f87171" }}>
            {decided === "accepted" ? "Proposition acceptée" : "Proposition déclinée"}
          </div>
          <p className="text-[var(--gai-grey-700)]">
            {decided === "accepted"
              ? "Merci pour votre confiance. Notre équipe revient vers vous pour formaliser le démarrage."
              : "Merci pour votre retour. Nous restons à votre disposition pour ajuster cette proposition."}
          </p>
        </div>
      ) : (
        <div className="mt-10 space-y-4" data-testid="proposal-actions">
          <textarea
            placeholder="Note (optionnel) — ajustements souhaités, questions…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            className="gai-input resize-none"
            data-testid="proposal-note"
          />
          <div className="flex gap-3 flex-wrap">
            <button onClick={() => decide("accepted")} disabled={status === "sending"} className="gai-btn-gold" data-testid="proposal-accept">
              Accepter la proposition
            </button>
            <button onClick={() => decide("declined")} disabled={status === "sending"} className="gai-btn-outline" data-testid="proposal-decline">
              Décliner
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
